// Budget solver for the guided ("new to sim racing") flow.
//
// Pure and offer-driven: it takes the live offers plus the wizard answers and
// returns a set of selections. No React, no fetching — so the same function that
// drives the UI can be unit-tested against a fixture catalog.
//
// The strategy is "cheapest complete build, then spend what is left". Starting
// from a complete rig and upgrading one notch at a time (rather than picking each
// component independently) guarantees two things a per-component pick cannot:
// the build is always affordable, and the money goes to the parts that matter
// most in a sim rig first — wheel base, then pedals, then screens.

import {
  thirdPartyCents,
  THIRD_PARTY_FX,
  PC_OPTIONS,
  SCREEN_OPTIONS,
  KEYBOARD_OPTIONS,
  SHIPPING_OPTIONS,
  ASSEMBLY_OPTIONS,
} from './catalog'
import { getShopItemPrice } from '../../../utils/shopItemsAndCart'
import { isOutOfStock } from '../../../utils/shopItems'

const cents = (o) => {
  const p = o && getShopItemPrice(o)
  return p ? p.priceCents : 0
}

// Order matters: earlier entries get upgraded first, so a mid-range budget buys
// a stronger wheel base before it buys a nicer monitor.
export const UPGRADE_PRIORITY = ['wheelbase', 'pedals', 'screen', 'pc', 'wheel', 'assembly']

// Under-16 riders are capped at the two lower torque tiers. 20 Nm through a
// direct-drive base is a lot of force for a young driver, and the uDFP20 can be
// unlocked later with a code once they grow into it — no hardware to re-buy.
export const MINOR_AGE = 16
export const MINOR_WHEELBASES = ['WHEEL_BASE_UPGRADABLE_TORQUE_6', 'WHEEL_BASE_UPGRADABLE_TORQUE_12']

const WHEELBASE_CANDIDATES = [
  'WHEEL_BASE_UPGRADABLE_TORQUE_6',
  'WHEEL_BASE_UPGRADABLE_TORQUE_9',
  'WHEEL_BASE_UPGRADABLE_TORQUE_12',
  'WHEEL_BASE_UPGRADABLE_TORQUE_15',
  'DFP15_WHEEL_BASE',
  'WHEEL_BASE_UPGRADED_TORQUE_20',
]
const PEDAL_CANDIDATES = ['PEDALS_THROTTLE_AND_BRAKE', 'PEDALS_THROTTLE_AND_BRAKE_CLUTCH_PEDAL']
const WHEEL_CANDIDATES = [
  'LITE_FORMULA_STEERING_WHEEL',
  'R295_STEERING_WHEEL',
  'R295_STEERING_WHEEL_G2',
  'VRS_ASCHER_ULTIMATE_WHEEL_QR_CONNECT_WHEEL_ADAPTER',
]

// Ladders are sorted by the LIVE price rather than hardcoded, so a sale or a new
// tier reorders them on its own. Sold-out options never enter a ladder — the
// solver can only ever produce a build you can actually order today.
const ladder = (byType, types) =>
  types
    .map((t) => byType.get(t))
    .filter((o) => o && o.price && !isOutOfStock(o))
    .sort((a, b) => cents(a) - cents(b))
    .map((o) => o.type)

/**
 * @param {Map} byType            itemType -> offer
 * @param {object} answers        { age, budgetEur, seat, mountType, screenSize, layout }
 * @param {string} currency       page currency, for the third-party conversions
 * @param {string} rigType        the chosen rig colour
 * @param {string} mountKitType   the EUR 0 base mount kit, when one exists
 * @returns {{wheelbase:string|null, pedals:string|null, wheel:string|null, pc:string, screen:string|null, assembly:string, ladders:object}}
 */
export function solveBuild({ byType, answers, currency, rigType, mountKitType }) {
  // The budget bands are stated in EUR, so convert the ceiling with the same FX
  // table the third-party prices use. thirdPartyCents() is not reusable here: it
  // rounds each ITEM to a whole unit, which would round a EUR 4,000 ceiling into
  // meaninglessness.
  const budget = Math.round((answers.budgetEur || 0) * (THIRD_PARTY_FX[currency] || 1) * 100)

  const isMinor = typeof answers.age === 'number' && answers.age > 0 && answers.age < MINOR_AGE
  const wheelbaseTypes = ladder(byType, isMinor ? MINOR_WHEELBASES : WHEELBASE_CANDIDATES)
  const pedalTypes = ladder(byType, PEDAL_CANDIDATES)
  const wheelTypes = ladder(byType, WHEEL_CANDIDATES)

  // Screens are scoped to the size the rider asked for, so "closest to budget"
  // never quietly swaps a 43" plan for three cheap 27s.
  const family = answers.screenProfile ? SCREEN_OPTIONS[answers.screenProfile] || [] : []
  const screenTypes = family
    .filter((s) => !answers.screenSize || s.size === answers.screenSize)
    .slice()
    .sort((a, b) => a.priceEur * a.qty - b.priceEur * b.qty)
    .map((s) => s.id)

  const pcTypes = PC_OPTIONS.slice()
    .sort((a, b) => a.priceEur - b.priceEur)
    .map((p) => p.id)
  const assemblyTypes = ASSEMBLY_OPTIONS.slice()
    .sort((a, b) => a.priceEur - b.priceEur)
    .map((a) => a.id)

  const ladders = {
    wheelbase: wheelbaseTypes,
    pedals: pedalTypes,
    wheel: wheelTypes,
    screen: screenTypes,
    pc: pcTypes,
    assembly: assemblyTypes,
  }

  // Everything the answers already fixed. These are not negotiable against the
  // budget — the rider asked for them.
  const fixedCents = (() => {
    let sum = 0
    const addType = (t) => {
      const o = t && byType.get(t)
      if (o) sum += cents(o)
    }
    addType(rigType)
    if (answers.seat) addType('GT2_SEAT')
    addType(answers.mountType)
    addType(mountKitType)
    if (answers.extraWheels > 0 && answers.adapterType) {
      const o = byType.get(answers.adapterType)
      if (o) sum += cents(o) * answers.extraWheels
    }
    const kb = KEYBOARD_OPTIONS[0]
    if (kb) sum += thirdPartyCents(kb.priceEur, currency)
    const ship = SHIPPING_OPTIONS[0]
    if (ship) sum += thirdPartyCents(ship.priceEur, currency)
    return sum
  })()

  const costOf = (key, id) => {
    if (id == null) return 0
    if (key === 'pc') {
      const p = PC_OPTIONS.find((x) => x.id === id)
      return p ? thirdPartyCents(p.priceEur, currency) : 0
    }
    if (key === 'assembly') {
      const a = ASSEMBLY_OPTIONS.find((x) => x.id === id)
      return a ? thirdPartyCents(a.priceEur, currency) : 0
    }
    if (key === 'screen') {
      const s = family.find((x) => x.id === id)
      return s ? thirdPartyCents(s.priceEur, currency) * s.qty : 0
    }
    return cents(byType.get(id))
  }

  // Cheapest complete build first. If even this exceeds the budget we still
  // return it — telling someone their budget is short is more useful than
  // silently handing them an empty rig.
  const pick = {}
  UPGRADE_PRIORITY.forEach((key) => {
    const list = ladders[key] || []
    pick[key] = list.length ? list[0] : null
  })

  const totalOf = (p) => UPGRADE_PRIORITY.reduce((sum, key) => sum + costOf(key, p[key]), fixedCents)

  // Spend what is left, one notch at a time, highest-priority component first.
  // Restarting the sweep after every accepted upgrade keeps the priority order
  // meaningful: the wheel base takes every notch it can afford before the PC
  // gets its first.
  let improved = true
  let guard = 0
  while (improved && guard < 64) {
    improved = false
    guard += 1
    for (const key of UPGRADE_PRIORITY) {
      const list = ladders[key] || []
      const at = list.indexOf(pick[key])
      if (at === -1 || at >= list.length - 1) continue
      const next = { ...pick, [key]: list[at + 1] }
      if (totalOf(next) <= budget) {
        pick[key] = list[at + 1]
        improved = true
        break
      }
    }
  }

  return {
    ...pick,
    ladders,
    isMinor,
    estimatedTotal: totalOf(pick),
    overBudget: totalOf(pick) > budget,
  }
}
